// NostoFacets — renders Nosto facets by cloning Liquid <template> elements.
// Filter/sort state stored in-memory. Full re-render on every response.
//
(function () {
  'use strict';

  // ── State ──

  let activeFilters = [];
  let activeSort = undefined;

  // ── Constants ──

  // 12 divides evenly into both the 4-col (size) and 3-col (color/functionality/
  // product type) grids, so a group always fills complete rows before "more".
  const SHOW_MORE_THRESHOLD = 12;

  const SIZE_ORDER = [
    '2XS', 'XS', 'S', 'S/M', 'M', 'L', 'L/XL', 'XL', '2XL', '3XL', '4XL', '5XL'
  ];

  // Token → original combo strings, refreshed on every render.
  let TOKEN_COMBOS = {};
  const STORAGE_KEY = 'nosto-facets-tokens';

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) TOKEN_COMBOS = JSON.parse(stored) || {};
  } catch (e) { /* ignore */ }

  const persistTokenCombos = () => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(TOKEN_COMBOS)); }
    catch (e) { /* ignore */ }
  };

  // Collapsible toggle — manages open="true" string for theme CSS selectors.
  const toggleDetails = (d) => {
    if (d.getAttribute('open') === 'true') {
      d.removeAttribute('open');
      const body = d.querySelector('[data-collapsible-body]');
      if (body) body.style.height = '0';
      const content = d.querySelector('[data-collapsible-content]');
      if (content) {
        content.style.opacity = '0.001';
        content.style.transform = 'translateY(-20px)';
      }
    } else {
      d.setAttribute('open', 'true');
      const body = d.querySelector('[data-collapsible-body]');
      if (body) body.style.height = 'auto';
      const content = d.querySelector('[data-collapsible-content]');
      if (content) {
        content.style.opacity = '1';
        content.style.transform = 'none';
      }
    }
  };

  // Shopify sort value → Nosto sort spec. activeSort stores the Shopify value, so
  // any sort stays selected and round-trips via `sort_by`. Unlisted values use
  // Nosto's default order; DEFAULT_SORT is that default (Featured) — no param.
  const SORT_MAP = {
    'best-selling':       [{ field: 'stats.buys', order: 'desc' }],
    'price-ascending':    [{ field: 'price', order: 'asc' }],
    'price-descending':   [{ field: 'price', order: 'desc' }],
    'title-ascending':    [{ field: 'name',  order: 'asc' }],
    'title-descending':   [{ field: 'name',  order: 'desc' }],
    'created-descending': [{ field: 'datePublished', order: 'desc' }],
    'created-ascending':  [{ field: 'datePublished', order: 'asc' }]
  };
  const DEFAULT_SORT = 'manual';

  // ── Templates ──

  const tplOptions    = () => document.getElementById('tpl-nosto-facet-options');
  const tplOptionsRow = () => document.getElementById('tpl-nosto-facet-options-row');
  const tplSort     = () => document.getElementById('tpl-nosto-facet-sort');

  // ── Helpers ──

  // Mirror Liquid's `| handle`: lowercase, non-alphanumerics → single dash, trim dashes.
  const handleize = (s) => (s == null ? '' : String(s))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  // Row appearance only: color → swatch box, size → box, everything else →
  // checkbox. The section class is derived from the facet name (below), so it is
  // NOT set here.
  const classifyFacet = (name) => {
    const n = (name || '').toLowerCase();
    if (n.indexOf('colour') !== -1 || n.indexOf('color') !== -1 || n.indexOf('litur') !== -1) return 'color';
    if (n === 'size' || n.indexOf('stærð') !== -1 || n.indexOf('staerd') !== -1) return 'size';
    return 'default';
  };

  // The add/remove/disable values are the theme's own selected/disabled state
  // classes (identical to what the default S&D filter emits), so rows show the
  // checked/disabled states correctly.
  const FACET_STYLE = {
    color:   { item: 'hide__checkbox collection__item', add: 'link--add-color',         remove: 'link--remove-color',        disable: 'link--disable-with-border', swatch: true,  count: true },
    size:    { item: 'hide__checkbox collection__item', add: 'link--add-size',          remove: 'link--remove-size',         disable: 'link--disable-with-border', swatch: false, count: true },
    default: { item: 'sidebar__item collection__item',  add: 'link--add-functionality', remove: 'link--remove-functionality', disable: 'link--disable',             swatch: false, count: false }
  };

  // ── State accessors ──

  const getFilters = () => activeFilters;
  const getSort = () => (activeSort && SORT_MAP[activeSort]) || undefined;

  // ── Tokenisation (split JSON-array combo values into individual tokens) ──
  // Auto-detects fields whose values are JSON-encoded arrays (e.g. '["Down jackets"]')
  // and unwraps them so users see clean labels. Multi-value combos are split into
  // individual tokens with aggregated counts.

  const tokeniseFacet = (facet) => {
    // Detect: does any row value parse as a JSON array?
    let hasJsonArrays = false;
    for (let j = 0; j < facet.data.length; j++) {
      const v = facet.data[j].value;
      if (v && v.charAt(0) === '[') {
        try {
          const test = JSON.parse(v);
          if (Array.isArray(test)) { hasJsonArrays = true; break; }
        } catch (e) { /* not JSON */ }
      }
    }
    if (!hasJsonArrays) return facet;

    const counts = {};
    const combos = {};
    let selected = [];
    for (let i = 0; i < activeFilters.length; i++) {
      if (activeFilters[i].field === facet.field && activeFilters[i].value) {
        selected = activeFilters[i].value;
        break;
      }
    }

    facet.data.forEach((row) => {
      let arr;
      try { arr = JSON.parse(row.value); } catch (e) { arr = null; }
      if (!Array.isArray(arr) || arr.length === 0) return;

      arr.forEach((token) => {
        counts[token] = (counts[token] || 0) + row.count;
        combos[token] = combos[token] || [];
        if (combos[token].indexOf(row.value) === -1) combos[token].push(row.value);
      });
    });

    TOKEN_COMBOS[facet.field] = combos;
    persistTokenCombos();

    const data = Object.keys(counts).sort().map((token) => ({
      value: token,
      count: counts[token],
      selected: selected.indexOf(token) !== -1
    }));

    return { id: facet.id, field: facet.field, type: facet.type, name: facet.name, data: data };
  };

  // Expand user-selected tokens back to original combo strings for API request (AND semantics).
  const expandFilters = (filters) => {
    return filters.map((f) => {
      if (!f.value) return f;
      const combos = TOKEN_COMBOS[f.field];
      if (!combos) return f;

      let matching = null;
      f.value.forEach((token) => {
        const perToken = combos[token] || [];
        matching = matching === null
          ? perToken.slice()
          : matching.filter((c) => perToken.indexOf(c) !== -1);
      });
      const expanded = (matching && matching.length) ? matching : f.value;
      return { field: f.field, value: expanded };
    });
  };

  // ── URL sync ──
  // Reflect filters & sort in the URL (like the default S&D filter) for
  // shareable/bookmarkable links. The param is `filter.<field>` taken straight
  // from the facet, so new filters need no code changes.
  const FILTER_PREFIX = 'filter.';
  const isFilterParam = (key) => key.indexOf(FILTER_PREFIX) === 0;

  // Write current filter + sort state into the URL (preserves page, q, etc.).
  const writeUrl = () => {
    if (!window.history || !window.URL) return;
    const url = new URL(location.href);
    const params = url.searchParams;

    Array.from(params.keys()).forEach((k) => {
      if (isFilterParam(k) || k === 'sort_by') params.delete(k);
    });

    activeFilters.forEach((f) => {
      if (!f.value || !f.value.length) return;
      const key = FILTER_PREFIX + f.field;
      f.value.forEach((v) => params.append(key, v));
    });

    if (activeSort && activeSort !== DEFAULT_SORT) params.set('sort_by', activeSort);

    history.replaceState(null, '', url);
  };

  // Restore filter + sort state from the URL (called once on load).
  const readUrl = () => {
    if (!window.URL) return;
    const params = new URL(location.href).searchParams;
    const byField = {};
    params.forEach((value, key) => {
      if (key === 'sort_by') {
        if (value) activeSort = value;
        return;
      }
      if (!isFilterParam(key)) return;
      const field = key.slice(FILTER_PREFIX.length);
      if (!field) return;
      if (!byField[field]) byField[field] = [];
      if (byField[field].indexOf(value) === -1) byField[field].push(value);
    });
    activeFilters = Object.keys(byField).map((field) => ({ field: field, value: byField[field] }));
  };

  // ── Renderers ──

  const renderOptionsFacet = (facet) => {
    const tpl = tplOptions();
    const rowTpl = tplOptionsRow();
    if (!tpl || !rowTpl) return null;

    const kind = classifyFacet(facet.name);
    const style = FACET_STYLE[kind];
    const gradients = window.NS_COLOR_GRADIENTS || {};

    let data = facet.data;

    // Sort sizes by SIZE_ORDER
    if (kind === 'size') {
      data = data.slice().sort((a, b) => {
        const ai = SIZE_ORDER.indexOf(a.value.toUpperCase());
        const bi = SIZE_ORDER.indexOf(b.value.toUpperCase());
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }

    const node = tpl.content.cloneNode(true);
    const root = node.querySelector('.filter-group');
    root.dataset.field = facet.field;
    root.querySelector('.filter-group__heading-label').textContent = facet.name;

    // Active count badge
    let activeCount = 0;
    data.forEach((v) => { if (v.selected) activeCount++; });
    const badge = root.querySelector('.filter-group__heading-values__size');
    if (activeCount > 0) {
      badge.textContent = activeCount;
      badge.style.display = '';
    }

    // Section class derived from the Nosto facet name (e.g. "Functionality" →
    // collection-functionality, "Product type" → collection-producttype). Any
    // facet — including new ones added in Nosto — gets a predictable CSS hook
    // automatically; style it in nosto.css, no JS change needed.
    const list = root.querySelector('.collection-nav');
    const nameClass = 'collection-' + facet.name.toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (nameClass !== 'collection-') list.classList.add(nameClass);

    // Rows
    data.forEach((v, i) => {
      const row = rowTpl.content.cloneNode(true);
      const li = row.querySelector('li');

      li.className = '';
      style.item.split(' ').forEach((c) => c && li.classList.add(c));
      if (v.selected) li.classList.add(style.remove);
      else if (v.count === 0) li.classList.add(style.disable);
      else li.classList.add(style.add);

      if (i >= SHOW_MORE_THRESHOLD) {
        li.classList.add('hidden');
        li.dataset.linkHidden = '';
      }

      const input = row.querySelector('input');
      input.dataset.value = v.value;
      if (v.selected) input.checked = true;
      if (v.count === 0 && !v.selected) input.disabled = true;

      // Color swatch bar (mirrors the default Color filter): use the group
      // gradient when the value is one of the configured groups, otherwise fall
      // back to the granular `--<handle>` swatch variable.
      if (style.swatch) {
        const handle = handleize(v.value);
        const grad = gradients[handle];
        const sw = document.createElement('div');
        // `swatches` carries the granular `--<handle>` colour variables
        // (from swatch-color-list.liquid) so the fallback var() resolves.
        sw.className = 'color__swatch color__swatch__style swatches';
        sw.style.backgroundImage = grad
          ? 'linear-gradient(' + grad + ')'
          : 'linear-gradient(var(--' + handle + '), var(--' + handle + '))';
        const inner = document.createElement('span');
        inner.className = 'color__swatch__color';
        sw.appendChild(inner);
        const rs = row.querySelector('radio-swatch');
        rs.insertBefore(sw, rs.querySelector('label'));
      }

      // Label
      row.querySelector('.filter__label-title').textContent = v.value;

      // Count
      const countEl = row.querySelector('.filter__label-active-labels');
      if (countEl) {
        if (style.count) {
          countEl.textContent = v.count;
          countEl.style.display = '';
        } else {
          countEl.remove();
        }
      }

      list.appendChild(row);
    });

    if (data.length > SHOW_MORE_THRESHOLD) {
      root.querySelector('[data-ns-show-more-wrap]').style.display = '';
    }

    return node;
  };

  const renderSortFacet = (container) => {
    const tpl = tplSort();
    const rowTpl = document.getElementById('tpl-nosto-facet-sort-row');
    if (!tpl || !rowTpl) return;

    const sortOptions = window.NS_SORT_OPTIONS || [];
    if (!sortOptions.length) return;

    const node = tpl.content.cloneNode(true);
    const list = node.querySelector('.collection-nav');

    sortOptions.forEach((opt) => {
      // "Most relevant" is already Nosto's default order — omit it here (it stays on the default drawer).
      if (opt.value === 'most-relevant') return;

      const row = rowTpl.content.cloneNode(true);
      const li = row.querySelector('li');
      const input = row.querySelector('input');
      input.setAttribute('data-ns-sort', opt.value);
      row.querySelector('.filter__label-title').textContent = opt.name;

      if (opt.value === activeSort) {
        input.checked = true;
        li.classList.remove('link--add-functionality');
        li.classList.add('link--remove-functionality');
      }

      list.appendChild(row);
    });

    container.appendChild(node);
  };

  // ── Main render ──

  // Capture per-group UI state (collapsed + "show more" expanded) so a full
  // re-render doesn't reset the drawer — this is what previously caused every
  // "more options" group to snap shut when a value was selected.
  const snapshotState = (container) => {
    const snap = {};
    container.querySelectorAll('.filter-group').forEach((g) => {
      const key = g.dataset.field || '__sort__';
      const d = g.querySelector('[data-collapsible]');
      snap[key] = {
        collapsed: d ? d.getAttribute('open') !== 'true' : false,
        // Only true when the user actually clicked "show more" — the wrap's own
        // display can't be used (it's hidden by default for short groups).
        expanded: g.dataset.nsExpanded === 'true'
      };
    });
    return snap;
  };

  const applyState = (frag, key, snap) => {
    const st = snap[key];
    if (!st) return;
    const g = frag.querySelector('.filter-group');
    if (!g) return;

    if (st.collapsed) {
      const d = g.querySelector('[data-collapsible]');
      if (d) {
        d.removeAttribute('open');
        const body = d.querySelector('[data-collapsible-body]');
        if (body) body.style.height = '0';
        const content = d.querySelector('[data-collapsible-content]');
        if (content) {
          content.style.opacity = '0.001';
          content.style.transform = 'translateY(-20px)';
        }
      }
    }

    if (st.expanded) {
      g.dataset.nsExpanded = 'true';
      const wrap = g.querySelector('[data-ns-show-more-wrap]');
      if (wrap) wrap.style.display = 'none';
      g.querySelectorAll('[data-link-hidden]').forEach((li) => li.classList.remove('hidden'));
    }
  };

  // Active filter chips — same placement as the default S&D filter: first child
  // of .collection__sidebar__buttons, above the "See N products" button.
  const renderActiveFilters = (container) => {
    const sidebar = container.closest('[data-collection-sidebar]');
    const buttonsWrap = sidebar && sidebar.querySelector('.collection__sidebar__buttons');
    if (!buttonsWrap) return;

    // The buttons wrap isn't re-rendered, so remove any previous block first.
    const existing = buttonsWrap.querySelector('.collection__active__filters');
    if (existing) existing.remove();

    const rows = [];
    activeFilters.forEach((f) => {
      (f.value || []).forEach((v) => rows.push({ field: f.field, value: v }));
    });
    if (!rows.length) return;

    const wrapTpl  = document.getElementById('tpl-nosto-active-filters');
    const chipTpl  = document.getElementById('tpl-nosto-active-filter-chip');
    const clearTpl = document.getElementById('tpl-nosto-active-filter-clear');
    if (!wrapTpl || !chipTpl) return;

    const wrap = wrapTpl.content.cloneNode(true).querySelector('.collection__active__filters');

    // "Clear filters" only when more than one value is active (matches default).
    if (rows.length > 1 && clearTpl) {
      wrap.appendChild(clearTpl.content.cloneNode(true));
    }

    rows.forEach((r) => {
      const chip = chipTpl.content.cloneNode(true);
      const a = chip.querySelector('a');
      a.dataset.nsField = r.field;
      a.dataset.nsValue = r.value;
      a.querySelector('small').textContent = r.value;
      wrap.appendChild(chip);
    });

    buttonsWrap.insertBefore(wrap, buttonsWrap.firstChild);
  };

  const renderFacets = (facets, container) => {
    const snap = snapshotState(container);

    container.replaceChildren();

    const sortFrag = document.createDocumentFragment();
    renderSortFacet(sortFrag);
    applyState(sortFrag, '__sort__', snap);
    container.appendChild(sortFrag);

    facets.forEach((f) => {
      let node;
      if (f.type === 'terms' && f.data && f.data.length) {
        const processed = tokeniseFacet(f);
        if (processed.data && processed.data.length) node = renderOptionsFacet(processed);
      }
      if (node) {
        applyState(node, f.field, snap);
        container.appendChild(node);
      }
    });

    renderActiveFilters(container);

    container.classList.add('ns-facets-ready');
  };

  // ── Event binding (called once) ──

  const bindEvents = (root, onSearch) => {
    // Sync the URL before every search so the querystring always matches state.
    const search = () => { writeUrl(); onSearch(); };

    // Active filter chips live in .collection__sidebar__buttons (outside root),
    // so delegate their clicks from the shared sidebar container.
    const sidebar = root.closest('[data-collection-sidebar]');
    if (sidebar) {
      sidebar.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-ns-remove-filter]');
        if (chip) {
          e.preventDefault();
          activeFilters = toggleFilter(activeFilters.slice(), chip.dataset.nsField, chip.dataset.nsValue, false);
          search();
          return;
        }
        const clearAll = e.target.closest('[data-ns-clear-filters]');
        if (clearAll) {
          e.preventDefault();
          activeFilters = [];
          search();
        }
      });
    }

    // Click delegation: collapsible toggle + show more
    root.addEventListener('click', (e) => {
      // Collapsible toggle
      const trigger = e.target.closest('[data-collapsible-trigger]');
      if (trigger) {
        e.preventDefault();
        const d = trigger.closest('[data-collapsible]');
        if (d) toggleDetails(d);
        return;
      }

      // Show more
      const showMore = e.target.closest('[data-ns-show-more]');
      if (!showMore) return;
      e.preventDefault();
      const wrap = showMore.closest('[data-ns-show-more-wrap]');
      if (wrap) wrap.style.display = 'none';
      const group = showMore.closest('.filter-group');
      if (group) {
        group.dataset.nsExpanded = 'true';
        group.querySelectorAll('[data-link-hidden]').forEach((li) => {
          li.classList.remove('hidden');
        });
      }
    });

    // Sort radio deselect: clicking the active sort option reverts to default
    root.addEventListener('click', (e) => {
      const input = e.target.closest('input[data-ns-sort]');
      if (!input) return;
      if (input.dataset.nsWasChecked === 'true') {
        // Deselect: revert to Nosto default sort
        input.checked = false;
        input.dataset.nsWasChecked = 'false';
        activeSort = undefined;
        search();
        e.preventDefault();
        return;
      }
      // Mark all sort radios as not-was-checked, then mark this one
      root.querySelectorAll('input[data-ns-sort]').forEach((r) => {
        r.dataset.nsWasChecked = 'false';
      });
      input.dataset.nsWasChecked = 'true';
    });

    // Sort radios and facet checkboxes
    root.addEventListener('change', (e) => {
      const input = e.target;

      if (input.hasAttribute('data-ns-sort')) {
        activeSort = input.getAttribute('data-ns-sort') || undefined;
        search();
        return;
      }

      if (input.dataset.value === undefined) return;
      const facetEl = input.closest('[data-field]');
      if (!facetEl) return;

      activeFilters = toggleFilter(activeFilters.slice(), facetEl.dataset.field, input.dataset.value, input.checked);
      search();
    });

    // Close button — dispatch theme:filter:close
    const section = root.closest('[data-section-type]');
    if (section) {
      section.addEventListener('click', (e) => {
        if (!e.target.closest('[data-collection-sidebar-close]')) return;
        e.preventDefault();
        section.dispatchEvent(new CustomEvent('theme:filter:close', { bubbles: true }));
      });
    }
  };

  // ── Filter state helper ──

  const toggleFilter = (filters, field, value, selected) => {
    let idx = -1;
    filters.forEach((f, i) => { if (f.field === field && f.value) idx = i; });

    if (selected) {
      if (idx > -1) {
        const vals = filters[idx].value.slice();
        if (vals.indexOf(value) === -1) vals.push(value);
        filters[idx] = { field: field, value: vals };
      } else {
        filters.push({ field: field, value: [value] });
      }
    } else if (idx > -1) {
      const vals = filters[idx].value.filter((v) => v !== value);
      if (vals.length) filters[idx] = { field: field, value: vals };
      else filters.splice(idx, 1);
    }

    return filters;
  };

  // ── Public API ──

  window.NostoFacets = {
    render: renderFacets,
    bindEvents: bindEvents,
    getFilters: getFilters,
    getSort: getSort,
    expandFilters: expandFilters,
    readUrl: readUrl,
    writeUrl: writeUrl
  };
})();
